import type { Option } from "@tento-lona/sheets";
import type { DateTime, NaiveDate, Utc } from "@tento-chrono";
import type { CellEntry, CellId, CellValue, TimepointCellEntry } from "@tento-lona/sheets";
import type { Actor } from "../invalidation.ts";
import type { RowData } from "./row-data.ts";
import { type CellWriteHost, CellWrites, type HeldCellWrites } from "./cell-writes.ts";

/**
 * What a store change says to the cache viewing it: the cell (its days, its
 * day or range, its id), what it holds now (`value`, `null`: cleared), the
 * actor the change carries (`null`: a server value), and its provenance — a
 * server snapshot install (`install`) or a server read's ingest (`server`) is
 * no local write.
 */
export interface StoreChange {
  readonly kind: "date" | "timepoint";
  readonly range: NaiveDate.Range;
  /** The date-keyed cell's day (`kind: "date"`); `null` for a timepoint cell. */
  readonly date: Option<NaiveDate.Partial>;
  /** The timepoint cell's range (`kind: "timepoint"`); `null` for a date-keyed cell. */
  readonly dtr: Option<DateTime.Range<Utc>>;
  /** The cell's id: a multi-cardinality date cell's or a timepoint cell's; `null` otherwise. */
  readonly id: Option<CellId>;
  /** What the cell holds now; `null`: the change cleared it. */
  readonly value: CellValue;
  /** Who wrote it: the change's actor, else the link's bridge actor; `null` for a server read's ingest. */
  readonly actor: Option<Actor>;
  /** Inside a server-snapshot install bracket (`asInstall`). */
  readonly install: boolean;
  /** A server read's ingest (the link's `serverActor`). */
  readonly server: boolean;
}

/**
 * The store a holding sheet installed into a row cache — the reads, writes
 * and mode of `CellStoreView` that choose the cache's authority (stated
 * here, by shape, so this module does not import the cells layer above it).
 */
export interface HeldCellStore extends HeldCellWrites {
  /** Which store: the holding sheet's (`local`) or the client's canonical one. */
  readonly store: "local" | "canonical";
  /** A client-owned authority (canonical or ordinary) that outlives its sheets. */
  readonly clientScoped: boolean;
  /** The row's date-keyed and timepoint cells in the store, counted without listing them. */
  cellCount(): number;
  /** The holding sheet derives this row's values (a sheet-scoped derived source). */
  readonly sheetScoped: boolean;
  /** The row's computed load, `null` for a stored row. */
  readonly compute: Option<(range: NaiveDate.Range) => Promise<void>>;
  /** The row's own cells join the store as a snapshot (the store's cells win). */
  adopt(dates: readonly CellEntry[], timepoints: readonly TimepointCellEntry[]): void;
  /** Register an outstanding read at the current local-write revision; pair with `releaseRevision`. */
  captureRevision(): number;
  /** The read captured at `rev` installed or bailed. */
  releaseRevision(rev: number): void;
}

/**
 * The row cache's own cells: what the server sent (`fetched`) and, for a
 * computed row, what it computed (`derived`). `mergesDerived` is true for a
 * formula row, whose computed cells overlay its fetched ones; any other
 * computed row reads its derived cells alone.
 */
export interface OwnCells {
  readonly fetched: RowData;
  derived(): Option<RowData>;
  mergesDerived(): boolean;
}

/**
 * Which store answers a `RowCellCache`'s cells — one implementation per mode,
 * chosen once when the holder installs or removes a view (`setView`), so no
 * read or install path re-derives the mode from the view's flags:
 *
 * - `own`: no sheet holds the row; its own cells answer every read.
 * - `held`: a sheet holds the row; the view's store answers every read.
 *   - `held` by the sheet (`client: false`): a server read ALSO lands in the
 *     row's own cells. That copy is not dead: it answers whether a read
 *     changed the row (`receiveBatchData`'s result, which drives the row-range
 *     notification) and it is what the row reads, marked stale, once its last
 *     sheet lets go (`setView(null)`) — the receive-authority
 *     characterization pins both.
 *   - `held` by the client (`client: true`, canonical or ordinary authority):
 *     the store is the only copy.
 */
export interface CellAuthority {
  readonly mode: "own" | "held";
  /** Where this mode's writes land (`CellWrites`): the row's own cells, or the view's store. */
  readonly writes: CellWrites;
  /** The holding sheet's store view, `null` for `own`. */
  readonly view: Option<HeldCellStore>;
  /** A server read also lands in the row's own cells (see `held` by the sheet). */
  readonly shadowsReads: boolean;
  /** The store is a client-owned authority: it alone holds the facts. */
  readonly clientOwned: boolean;
  /** The cache's own fetch installs its answer into `view` (a canonical or client-owned store). */
  readonly installsOwnFetch: boolean;
  /** The holding sheet computes the row's cells (the cache asks it rather than fetching). */
  readonly sheetComputes: boolean;
  /** Date-keyed cells in `range` (`null`: all). */
  dates(range: Option<NaiveDate.Range>): CellEntry[];
  /** Timepoint cells in `range` (`null`: all). */
  timepoints(range: Option<DateTime.Range<Utc>>): TimepointCellEntry[];
  /** The date-keyed cells an index is built from. */
  indexCells(): CellEntry[];
  /** The date-keyed cells an index is built from, in `range` only. */
  indexCellsIn(range: NaiveDate.Range): CellEntry[];
  /** Cells held (date-keyed and timepoint): what an idle row costs. */
  cellCount(): number;
}

export namespace CellAuthority {
  /** The row's own cells answer and take the writes (no sheet holds the row); `host` is the owning cache. */
  export function own(cells: OwnCells, host: CellWriteHost): CellAuthority {
    return new OwnAuthority(cells, CellWrites.own(cells.fetched, host));
  }

  /** The store `view` a holding sheet installed answers and takes the writes; `host` is the owning cache. */
  export function held(view: HeldCellStore, host: CellWriteHost): CellAuthority {
    return new HeldAuthority(view, CellWrites.held(view, host));
  }
}

class OwnAuthority implements CellAuthority {
  readonly mode = "own";
  readonly view = null;
  readonly shadowsReads = true;
  readonly clientOwned = false;
  readonly installsOwnFetch = false;
  readonly sheetComputes = false;
  readonly writes: CellWrites;
  readonly #cells: OwnCells;

  constructor(cells: OwnCells, writes: CellWrites) {
    this.#cells = cells;
    this.writes = writes;
  }

  dates(range: Option<NaiveDate.Range>): CellEntry[] {
    const own = this.#cells;
    const fetched = range ? own.fetched.getAllInRange(range) : own.fetched.getAll();
    const derivedData = own.derived();
    if (!derivedData) return fetched;
    const derived = range ? derivedData.getAllInRange(range) : derivedData.getAll();
    if (!own.mergesDerived()) return derived;
    return mergeCellEntries(fetched, derived);
  }

  timepoints(range: Option<DateTime.Range<Utc>>): TimepointCellEntry[] {
    const own = this.#cells;
    const fetched = range ? own.fetched.getAllTimepointsInRange(range) : own.fetched.getAllCustom();
    const derivedData = own.derived();
    if (!derivedData) return fetched;
    const derived = range ? derivedData.getAllTimepointsInRange(range) : derivedData.getAllCustom();
    if (!own.mergesDerived()) return derived;
    return mergeCustomCellEntries(fetched, derived);
  }

  indexCells(): CellEntry[] {
    return (this.#cells.derived() ?? this.#cells.fetched).getAll();
  }

  indexCellsIn(range: NaiveDate.Range): CellEntry[] {
    return (this.#cells.derived() ?? this.#cells.fetched).getAllInRange(range);
  }

  cellCount(): number {
    return this.#cells.fetched.size + (this.#cells.derived()?.size ?? 0);
  }
}

class HeldAuthority implements CellAuthority {
  readonly mode = "held";
  readonly view: HeldCellStore;
  // The sub-mode is fixed when the holder installs the view (`setView`):
  // every view is composed (`retainAuthority`, a derived source's scope)
  // before it is installed, and a flag flipped afterwards does not move a
  // cache between sub-modes behind its reads and writes.
  readonly clientOwned: boolean;
  readonly shadowsReads: boolean;
  readonly installsOwnFetch: boolean;
  readonly sheetComputes: boolean;
  readonly writes: CellWrites;

  constructor(view: HeldCellStore, writes: CellWrites) {
    this.view = view;
    this.writes = writes;
    this.clientOwned = view.clientScoped;
    this.shadowsReads = !view.clientScoped;
    this.installsOwnFetch = view.clientScoped || view.store === "canonical";
    this.sheetComputes = view.sheetScoped && view.compute != null;
  }

  dates(range: Option<NaiveDate.Range>): CellEntry[] {
    return [...this.view.dates(range)];
  }

  timepoints(range: Option<DateTime.Range<Utc>>): TimepointCellEntry[] {
    return [...this.view.timepoints(range)];
  }

  indexCells(): CellEntry[] {
    return [...this.view.dates(null)];
  }

  indexCellsIn(range: NaiveDate.Range): CellEntry[] {
    return [...this.view.dates(range)];
  }

  cellCount(): number {
    return this.view.cellCount();
  }
}

function mergeCellEntries(fetched: CellEntry[], derived: CellEntry[]): CellEntry[] {
  if (fetched.length === 0) return derived;
  if (derived.length === 0) return fetched;
  const merged = new Map<string, CellEntry>();
  for (const entry of fetched) merged.set(cellEntryKey(entry), entry);
  for (const entry of derived) merged.set(cellEntryKey(entry), entry);
  return [...merged.values()].sort((a, b) => a.date.start.dse - b.date.start.dse);
}

function mergeCustomCellEntries(
  fetched: TimepointCellEntry[],
  derived: TimepointCellEntry[],
): TimepointCellEntry[] {
  if (fetched.length === 0) return derived;
  if (derived.length === 0) return fetched;
  const merged = new Map<string, TimepointCellEntry>();
  for (const entry of fetched) merged.set(entry.id, entry);
  for (const entry of derived) merged.set(entry.id, entry);
  return [...merged.values()].sort((a, b) => a.dtr.start.mse - b.dtr.start.mse);
}

function cellEntryKey(entry: CellEntry): string {
  return entry.id != null ? `id:${entry.id}` : `${entry.date.type}:${entry.date.index}`;
}
